import React, { useState, useMemo, createContext, Dispatch, SetStateAction } from 'react'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { createDefaultPaginationData, formatCount, formatMoney, createTableData } from '../../../utils/default/TableHelper'
import { PaginationRes, PaginationReq, ActivityType, ActivityDragonBoatFestival2023, ActivityDragonBoatFestival2023Query, ActivityDragonBoatFestival2023Sum, ActivityDragonBoatFestival2023StatusType } from '@golden/gdk-admin'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { searchToRequest } from './ActivityRecordDetailForm'
import { useRequestFromSearch, useGetDataByPayload, usePaginationClickAndChangeUrl } from '../../../utils/default/ComplexFlowHook'
import DateTime from '../../default/present/DateTime'
import ScrollablePaper from '../../default/present/ScrollablePaper'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'
import CoreTable from '../../default/present/CoreTable'
import allRoute, { Path } from '../route/route'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import MemoPopoverWithContext from '../../default/memo/MemoPopoverWithContext'
import { MemoPopoverPayload, createDefaultMemoPopoverPayload } from '../../default/memo/MemoPopover'
import { useLocation } from 'react-router-dom'
import { dragonBoatFestival2023StatusName } from '../../../constants/default/activityStatusName'

interface SumRowType1 {
  id: number
  totalCount: string
  noUse: string
  received: string
  expired: string
  lost: string
}

interface SumRowType2 {
  id: number
  totalBonus: string
  bonus288: string
  bonus88: string
  bonus28: string
  noBonus: string
}

interface RowType {
  id: number
  order: string
  sentTime: React.ReactElement
  account: string
  staffAccount: string
  content: string
  status: React.ReactElement | string
  bonus: string
  updatedAt: React.ReactElement
  updatedBy: string
  memo: string
}

const MemoContext = createContext<[MemoPopoverPayload, Dispatch<SetStateAction<MemoPopoverPayload>>]>([
  createDefaultMemoPopoverPayload(),
  () => {}
])

const ActivityRecordDragonBoatFestival2023Table: React.FC = () => {
  const classes = useCommonStyles()
  const { t } = useT()
  const gdk = useGDK()
  const activities = useGDKStore.activity.activities()
  const location = useLocation()
  const [list, setList] = useState<PaginationRes<ActivityDragonBoatFestival2023[]> & ActivityDragonBoatFestival2023Sum>({
    ...createDefaultPaginationData([]),
    total_count: 0,
    no_use_count: 0,
    expired_count: 0,
    lost_count: 0,
    received_count: 0,
    bonus_0_count: 0,
    bonus_28_count: 0,
    bonus_88_count: 0,
    bonus_288_count: 0,
    total_bonus: '0,0000'
  })
  const pageFlow = usePageFlow()
  const request = useRequestFromSearch({ searchToRequest })
  useGetDataByPayload({
    payload: request as ActivityDragonBoatFestival2023Query & PaginationReq,
    gdkFunc: (payload) => gdk.activity.getActivityRecordDragonBoatFestival2023(payload),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<ActivityDragonBoatFestival2023[]> & ActivityDragonBoatFestival2023Sum) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: request !== undefined
  })
  const tableClasses = useMemo(() => ({
    head: classes.greyTableHead,
    row: classes.tableRow,
    cellHead: classes.tableCellHead,
    cellBody: classes.nowrap
  }), [classes])
  const sumRow1 = useMemo(() => {
    return [{
      id: 1,
      totalCount: formatCount(list.total_count),
      noUse: formatCount(list.no_use_count),
      received: formatCount(list.received_count),
      lost: formatCount(list.lost_count),
      expired: formatCount(list.expired_count)
    }] as SumRowType1[]
  }, [list])
  const sumData1 = useMemo(() => {
    return createTableData<SumRowType1>(
      {
        id: {
          label: '',
          value: 'id'
        },
        totalCount: {
          label: t('common.activityTotalCount'),
          value: 'totalCount',
          align: 'center'
        },
        noUse: {
          label: t('common.unused'),
          value: 'noUse',
          align: 'center'
        },
        received: {
          label: t('common.winned'),
          value: 'received',
          align: 'center'
        },
        expired: {
          label: t('common.expired'),
          value: 'expired',
          align: 'center'
        },
        lost: {
          label: t('common.notWinned'),
          value: 'lost',
          align: 'center'
        }
      },
      [
        'totalCount',
        'noUse',
        'received',
        'lost',
        'expired'
      ],
      sumRow1,
      'id'
    )
  }, [t, sumRow1])
  const sumRow2 = useMemo(() => {
    return [{
      id: 1,
      bonus288: formatCount(list.bonus_288_count),
      bonus88: formatCount(list.bonus_88_count),
      bonus28: formatCount(list.bonus_28_count),
      noBonus: formatCount(list.lost_count),
      totalBonus: formatMoney(list.total_bonus)
    }] as SumRowType2[]
  }, [list])
  const sumData2 = useMemo(() => {
    return createTableData<SumRowType2>(
      {
        id: {
          label: '',
          value: 'id'
        },
        bonus288: {
          label: '288',
          value: 'bonus288',
          align: 'center'
        },
        bonus88: {
          label: '88',
          value: 'bonus88',
          align: 'center'
        },
        bonus28: {
          label: '28',
          value: 'bonus28',
          align: 'center'
        },
        noBonus: {
          label: t('common.thankCustom'),
          value: 'noBonus',
          align: 'center'
        },
        totalBonus: {
          label: t('common.totalActivityBonus'),
          value: 'totalBonus',
          align: 'center'
        }
      },
      [
        'totalBonus',
        'bonus288',
        'bonus88',
        'bonus28',
        'noBonus'
      ],
      sumRow2,
      'id'
    )
  }, [t, sumRow2])
  const rows = useMemo(() => {
    return list.data.map((item, index) => {
      return {
        id: index,
        order: item.serial_number,
        sentTime: (<DateTime time={item.created_at} />),
        account: item.account,
        staffAccount: item.staff_account,
        content: item.content,
        status: t(dragonBoatFestival2023StatusName[item.status]),
        bonus: (item.status === ActivityDragonBoatFestival2023StatusType.EXPIRED || item.status === ActivityDragonBoatFestival2023StatusType.NO_USE) ? '-' : formatMoney(item.bonus),
        updatedAt: item.updated_at ? (<DateTime time={item.updated_at} />) : (<p></p>),
        updatedBy: item.updated_by,
        memo: item.memo || ''
      } as RowType
    })
  }, [list.data, t])
  const data = useMemo(() => {
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        order: {
          label: t('common.activitySerial'),
          value: 'order',
          align: 'center'
        },
        sentTime: {
          label: t('common.applyTime'),
          value: 'sentTime',
          align: 'center'
        },
        account: {
          label: t('common.playerAccount'),
          value: 'account',
          align: 'center'
        },
        staffAccount: {
          label: t('common.staffAccount'),
          value: 'staffAccount',
          align: 'center'
        },
        content: {
          label: t('common.activityContent'),
          value: 'content',
          align: 'left'
        },
        status: {
          label: t('common.status'),
          value: 'status',
          align: 'center'
        },
        bonus: {
          label: t('common.activityBonus'),
          value: 'bonus',
          align: 'right'
        },
        updatedAt: {
          label: t('common.updatedAt'),
          value: 'updatedAt',
          align: 'center'
        },
        updatedBy: {
          label: t('common.updateBy'),
          value: 'updatedBy',
          align: 'center'
        },
        memo: {
          label: t('common.memo'),
          value: 'memo',
          align: 'center'
        }
      },
      [
        'order',
        'sentTime',
        'account',
        'staffAccount',
        'content',
        'status',
        'bonus',
        'updatedAt',
        'updatedBy',
        'memo'
      ],
      rows,
      'id'
    )
  }, [t, rows])
  const handlePagination = usePaginationClickAndChangeUrl({
    request,
    encodePath: (location.pathname === Path.ACTIVITY_RECORD_DETAIL) ? allRoute.activityRecordDetail.encodePath : allRoute.activityRecordSub.encodePath
  })
  return (
    <MemoPopoverWithContext memoPopoverContext={MemoContext}>
      <Box paddingBottom={3}>
        <ScrollablePaper marginX={5}>
          <Box padding={5}>
            <LoadingAndErrorFrame { ...pageFlow.status }>
              <Box
                paddingY={1.25}
                paddingX={2}
                className={classes.pinkTitleBar}
              >
                <Typography variant="h5">
                  {activities.find((item) => item.type === ActivityType.DRAGON_BOAT_FESTIVAL_2023)?.name ?? ''}
                </Typography>
              </Box>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box paddingY={2}>
                    <Typography variant="h5">
                      {t('common.activityStatisticResult')}
                    </Typography>
                  </Box>
                  <CoreTable
                    data={sumData1}
                    total={1}
                    classes={tableClasses}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box paddingY={2}>
                    <Typography variant="h5">
                      {t('common.bonusStatisticResult')}
                    </Typography>
                  </Box>
                  <CoreTable
                    data={sumData2}
                    total={1}
                    classes={tableClasses}
                  />
                </Grid>
              </Grid>
            </LoadingAndErrorFrame>
          </Box>
        </ScrollablePaper>
      </Box>
      <ScrollablePaper marginX={5}>
        <Box padding={5}>
          <LoadingAndErrorFrame { ...pageFlow.status }>
            <CoreTable
              data={data}
              total={list.total}
              classes={tableClasses}
              showPagination
              page={request?.page ?? 1}
              onChangePage={handlePagination}
            />
          </LoadingAndErrorFrame>
        </Box>
      </ScrollablePaper>
    </MemoPopoverWithContext>
  )
}

export default React.memo(ActivityRecordDragonBoatFestival2023Table)
